import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  DocumentTextIcon,
  ShieldCheckIcon,
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  CalendarIcon,
  UserIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const DentalConsentManagement = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  // Set page meta
  usePageMeta(
    'Dental Consent Management for UK Practices | Smile Consent',
    'Dental consent management for UK practices with workflow tracking, digital signing, audit trails, and AI-assisted consent creation for every treatment.',
    'https://smileconsent.co.uk/dental-consent-management'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const managementFeatures = [
    {
      icon: DocumentTextIcon,
      title: 'AI-assisted consent',
      description: 'Consent workflow with editable drafts — your practice is responsible for final wording and regulatory compliance',
      features: [
        'AI-assisted consent drafting',
        'Patient search (name, email, DOB, address)',
        'Multiple treatments per consent',
        'Digital patient signatures',
        'Consent activity records where the product provides them'
      ]
    },
    {
      icon: ChartBarIcon,
      title: 'Analytics & Reporting',
      description: 'Comprehensive consent analytics and compliance reporting',
      features: [
        'Consent completion rates',
        'Compliance metrics',
        'Form usage analytics',
        'CQC reporting tools',
        'Performance dashboards'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Documentation support',
      description: 'Clearer records and retrieval for consent — not automated regulatory certification',
      features: [
        'Themes aligned with common professional expectations',
        'Materials you can use when preparing for scrutiny',
        'Consistent workflows across the team',
        'You retain responsibility for legal and regulatory obligations',
        'Product updates from time to time'
      ]
    },
    {
      icon: ClockIcon,
      title: 'Workflow Automation',
      description: 'Streamlined consent processes that save time and reduce errors',
      features: [
        'Quick form selection',
        'Pre-filled patient data',
        'Review prompts before send',
        'Instant form completion',
        'Reduced paperwork time'
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'Team Management',
      description: 'Seamless collaboration between dental team members',
      features: [
        'Multi-user access',
        'Role-based permissions',
        'Real-time updates',
        'Team notifications',
        'Shared consent workflow'
      ]
    },
    {
      icon: CogIcon,
      title: 'System Integration',
      description: 'Seamless integration with existing practice systems',
      features: [
        'Practice workflow alignment',
        'Digital workflow automation',
        'Secure export & reporting',
        'Consistent templates across your team',
        'Encrypted data handling'
      ]
    }
  ];

  const managementStats = [
    {
      number: '95%',
      label: 'Time Saved',
      description: 'vs paper forms'
    },
    {
      number: '100%',
      label: 'Clinician sign-off',
      description: 'On AI-assisted sends before patient delivery'
    },
    {
      number: '24/7',
      label: 'System Access',
      description: 'Always available'
    },
    {
      number: '< 1 min',
      label: 'Per consent',
      description: 'AI-assisted draft, clinician-reviewed'
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Patient & Treatments',
      description: 'Search patient, add diagnosis, treatment, tooth and cost',
      time: '30 seconds',
      icon: DocumentTextIcon
    },
    {
      step: 2,
      title: 'Patient Review',
      description: 'Patient reviews consent form and treatment information',
      time: '3 minutes',
      icon: UserGroupIcon
    },
    {
      step: 3,
      title: 'Digital Signature',
      description: 'Patient provides digital signature and consent confirmation',
      time: '1 minute',
      icon: ClipboardDocumentCheckIcon
    },
    {
      step: 4,
      title: 'Compliance Check',
      description: 'Automated compliance verification and audit trail creation',
      time: 'Instant',
      icon: ShieldCheckIcon
    },
    {
      step: 5,
      title: 'Document Storage',
      description: 'Secure storage and compliance recording',
      time: 'Instant',
      icon: LockClosedIcon
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-green-100 via-blue-100 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Dental Consent Management for UK Practices
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Manage dental consent from draft to signature with workflow tracking, treatment-specific records, digital signing, and clear audit trails for UK practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Management Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {managementStats.map((stat, index) => (
              <div key={index} className="text-center animate-on-scroll">
                <div className="text-4xl md:text-5xl font-bold text-green-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-gray-900 mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Complete Consent Management Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage dental consent forms, ensure compliance, and streamline your practice workflow.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {managementFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 p-3 rounded-lg mr-4">
                    <feature.icon className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.features.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Streamlined Consent Workflow
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our 5-step process makes consent management simple, fast, and compliant.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {workflowSteps.map((step, index) => (
              <div key={index} className="text-center animate-on-scroll">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-8 w-8 text-green-600" />
                </div>
                <div className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 mb-2">
                  {step.description}
                </p>
                <div className="text-sm text-green-600 font-semibold">
                  {step.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Consent Management?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Join thousands of dental practices using our comprehensive consent management system. 
            Start your free trial today - no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-green-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <button 
              onClick={handleScheduleDemo}
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-green-600 transition-colors flex items-center justify-center gap-2"
            >
              <CalendarIcon className="h-6 w-6" />
              Book Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DentalConsentManagement;
